import TestRoutes from './test/_routes';
import session, { getActiveSession } from '@cognitoforms/api/services/session';
import { GlobalError } from '@cognitoforms/types/server-types/core/services/global-error';

export default [
	...TestRoutes,
	{
		path: '/404',
		name: 'page-not-found',
		component: () => import(/* webpackChunkName: 'PageNotFound' */ 'src/views/PageNotFound.vue')
	},
	{
		path: 'myaccount',
		component: () => import(/* webpackChunkName: 'Settings' */ './Admin.vue'),
		meta: {
			auth: 'required',
			isRouterLink: true
		},
		async beforeEnter(to, from, next) {
			if (session.value && session.value.enabledFeatures.ViewAsUser) {
				const cookie = $cookies?.get(`cognito.view-as-user.${session.value.organizationId}`);

				// Redirect if cookie is present
				if (cookie)
					next({ path: `/${session.value.orgCode}/home` });
				else
					next();
			}
			else
				next();
		},
		children: [
			{
				path: '',
				name: 'account-settings',
				component: () => import(/* */ 'src/views/settings/user/AccountSettings.vue'),
				meta: {
					auth: 'required',
					isRouterLink: true,
					useSettingsLayout: true,
					sections: [
						{
							id: '#profile',
							displayName: 'Profile'
						},
						{
							id: '#organizations',
							displayName: 'Organizations'
						},
						{
							id: '#delete-account',
							displayName: 'Delete Account'
						}
					]
				}
			}
		]
	},
	{
		path: '/forms/saveandcontinue',
		name: 'save-and-continue',
		component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
		props: route => ({ ...route.query, ...route.params }),
		meta: {
			auth: 'required'
		}
	},
	{
		path: '/:orgcode/settings/invoice',
		component: () => import(/* webpackChunkName: 'Settings' */ './Admin.vue'),
		meta: {
			auth: 'required'
		},
		children: [
			{
				path: ':id',
				name: 'organization-settings-invoice',
				component: () => import(/* webpackChunkName: 'Invoice' */ './Invoice.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Owner',
					isRouterLink: true,
					hideHeaderNav: true
				}
			}
		]
	},
	{
		path: '/:orgcode/settings/selectplan',
		component: () => import(/* webpackChunkName: 'Settings' */ './Admin.vue'),
		meta: {
			auth: 'required'
		},
		children: [
			{
				path: '',
				name: 'organization-settings-select-plan',
				component: () => import(/* webpackChunkName: 'SelectPlan' */ 'src/views/pricing/SelectPlan.vue'),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						// Include hash for prepay
						next({ name: 'organization-settings-select-plan-dialog', params: to.params, query: to.query, hash: to.hash });
					}
					else
						next();
				},
				props: true,
				meta: {
					auth: 'required',
					minimumRole: 'Owner',
					isRouterLink: true,
					loadManifest: true
				}
			}
		]
	},
	{
		path: '/:orgcode/settings',
		meta: {
			auth: 'required'
		},
		component: () => import(/* webpackChunkName: 'Settings' */ './Admin.vue'),
		children: [
			{
				path: '',
				name: 'organization-settings',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationGeneralSettings.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true
				}
			},
			{
				path: 'general',
				name: 'organization-settings-general',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationGeneralSettings.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'General'
				}
			},
			{
				path: 'plan',
				name: 'organization-settings-plan',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationPlanAndBilling.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Plan & Billing'
				},
				children: [
					{
						path: 'select',
						name: 'organization-settings-select-plan-dialog',
						component: () => import(/* webpackChunkName: 'SelectPlan' */ 'src/views/pricing/SelectPlan.vue'),
						props: true,
						meta: {
							auth: 'required',
							minimumRole: 'Administrator',
							useSettingsLayout: true,
							isRouterLink: true,
							loadManifest: true
						},
						async beforeEnter(to, from, next) {
							// [AppNav]: Remove with feature flag. Safeguard for legacy updrage links
							if (session.value && !session.value.enabledFeatures.AppNav) {
								// Include hash for prepay
								next({ name: 'organization-settings-select-plan', params: to.params, query: to.query, hash: to.hash });
							}
							else
								next();
						}
					},
					{
						path: 'subscription',
						name: 'organization-settings-subscription-settings-dialog',
						component: () => import(/* webpackChunkName: 'Settings' */ 'src/views/settings/organization/ChangePlan.vue'),
						props: route => ({ ...route.query, ...route.params }),
						meta: {
							auth: 'required',
							minimumRole: 'Administrator',
							isRouterLink: true,
							useSettingsLayout: true
						}
					},
					{
						path: 'confirmation/:id',
						name: 'organization-settings-change-plan-confirmation-dialog',
						component: () => import(/* webpackChunkName: 'ChangePlanConfirmation' */ './ChangePlanConfirmation.vue'),
						props: route => ({ ...route.query, ...route.params }),
						meta: {
							auth: 'required',
							minimumRole: 'Owner',
							isRouterLink: true,
							useSettingsLayout: true
						}
					},
					{
						path: 'prepay-check-confirmation/:id',
						name: 'organization-settings-prepay-check-confirmation-dialog',
						component: () => import(/* webpackChunkName: 'PrepayCheckConfirmation' */ './PrepayCheckConfirmation.vue'),
						props: route => ({ ...route.query, ...route.params }),
						meta: {
							auth: 'required',
							minimumRole: 'Owner',
							isRouterLink: true,
							useSettingsLayout: true
						}
					}
				]
			},
			{
				path: 'usage',
				name: 'organization-settings-usage',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationUsagePage.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Usage'
				}
			},
			{
				path: 'users',
				name: 'organization-settings-users',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationUsersAndAuthentication.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Users & Authentication'
				}
			},
			{
				path: 'guest-access',
				name: 'organization-settings-guest-access',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationGuestAccess.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Guest Access'
				}
			},
			{
				path: 'appearance',
				name: 'organization-appearance',
				component: () => import (/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationAppearance.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Appearance'
				},
				beforeEnter: async (to, from, next) => {
					const session = await getActiveSession();
					if (session && !session.enabledFeatures.OrgAppearance)
						next({ path: `/${session.orgCode}/settings/general` });
					else
						next();
				}
			},
			{
				path: 'email-domains',
				name: 'organization-settings-email-domains',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationEmailDomains.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Email Domains'
				}
			},
			{
				path: 'integrations',
				name: 'organization-settings-integrations',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/IntegrationsList.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Integrations'
				},
				beforeEnter: async (to, _, next) => {
					const session = await getActiveSession();
					to.params.useAppNav = session?.enabledFeatures?.AppNav ?? false;
					next();
				}
			},
			{
				path: 'referrals',
				name: 'organization-settings-referrals',
				component: () => import(/* webpackChunkName: 'Settings' */ '../settings/organization/OrganizationReferralSettings.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					useSettingsLayout: true,
					displayName: 'Referrals'
				}
			},
			{
				path: 'preview-features',
				name: 'organization-settings-preview-features',
				component: () => import(/* webpackChunkName: 'Settings' */ 'src/views/organization/FeaturePreview.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Owner',
					useSettingsLayout: true,
					displayName: 'Preview Features'
				},
				beforeEnter: async (to, _, next) => {
					const session = await getActiveSession();
					to.params.useAppNav = session?.enabledFeatures?.AppNav ?? false;
					to.params.organizationId = session?.organizationId ?? '';
					to.params.orgCode = session?.orgCode ?? '';
					to.params.userEmail = session?.userEmail ?? '';
					to.params.userName = session?.userName ?? '';
					next();
				}
			},
			{
				path: 'subscription',
				name: 'organization-settings-subscription-settings',
				component: () => import(/* webpackChunkName: 'Settings' */ 'src/views/settings/organization/ChangePlan.vue'),
				props: route => ({ ...route.query, ...route.params }),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						next({ name: 'organization-settings-subscription-settings-dialog', params: to.params, query: to.query });
					}
					else
						next();
				},
				meta: {
					auth: 'required',
					minimumRole: 'Owner',
					useSettingsLayout: false
				}
			},
			{
				path: 'users/:id',
				name: 'organization-settings-profile-settings',
				component: () => import(/* webpackChunkName: 'ProfileSettings' */ './ProfileSettings.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator',
					isRouterLink: true,
					useSettingsLayout: true
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						next({ name: 'organization-settings-users', params: to.params, query: to.query });
					}
					else
						next();
				}

			},
			{
				path: 'change-plan-confirmation/:id',
				name: 'organization-settings-change-plan-confirmation',
				component: () => import(/* webpackChunkName: 'ChangePlanConfirmation' */ './ChangePlanConfirmation.vue'),
				props: route => ({ ...route.query, ...route.params }),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						next({ name: 'organization-settings-change-plan-confirmation-dialog', params: to.params, query: to.query });
					}
					else
						next();
				},
				meta: {
					auth: 'required',
					minimumRole: 'Owner',
					isRouterLink: true,
					useSettingsLayout: false
				}
			},
			{
				path: 'prepay-check-confirmation/:id',
				name: 'organization-settings-prepay-check-confirmation',
				component: () => import(/* webpackChunkName: 'PrepayCheckConfirmation' */ './PrepayCheckConfirmation.vue'),
				props: route => ({ ...route.query, ...route.params }),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						next({ name: 'organization-settings-prepay-check-confirmation-dialog', params: to.params, query: to.query });
					}
					else
						next();
				},
				meta: {
					auth: 'required',
					minimumRole: 'Owner',
					isRouterLink: true,
					useSettingsLayout: true
				}
			},
			{
				path: 'global-store-debug',
				name: 'global-store-debug',
				component: () => import(/* webpackChunkName: 'GlobalStoreDebug' */ './developer/GlobalStoreDebug.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					isRouterLink: true
				}
			},
			{
				path: ':catchAll(.*)',
				name: 'organization-settings-other',
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					minimumRole: 'Administrator'
				},
				beforeEnter(to, from, next) {
					next({ name: 'organization-settings-general', params: to.params, query: to.query });
				}
			}
		]
	},
	{
		path: '/:orgcode',
		component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
		meta: { auth: 'required' },
		children: [
			{
				path: '',
				name: 'forms',
				component: () => import(/* webpackChunkName: 'FormsDashboard' */ './FormsDashboard.vue'),
				props: route => ({ ...route.query, ...route.params }),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						const components = await import(/* webpackChunkName: 'AppHome' */ './AppHome.vue');
						to.matched[to.matched.length - 1].components = components;
					}
					next();
				},
				meta: {
					auth: 'required',
					isRouterLink: true,
					showLoadingIndicator: true,
					showGlobalErrors: true,
					mobileMenu: true,
					showAppNavOptIn: true
				}
			},
			{
				path: 'home',
				name: 'home',
				component: () => import(/* webpackChunkName: 'FormsDashboard' */ './AppHome.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: { auth: 'required', showGlobalErrors: true }
			},
			{
				path: 'tasks',
				name: 'task-dashboard',
				component: () => import(/* webpackChunkName: 'TaskDashboard' */ './TaskDashboard.vue'),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav)
						next();
					else
						next('/' + to.params.orgcode);
				},
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required'
				}
			},
			{
				path: 'new/:templateName?',
				name: 'builder-new',
				component: () => import(/* webpackChunkName: 'FormBuilder' */ './FormBuilder.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					isRouterLink: true,
					showLoadingIndicator: true,
					showFormHeader: true,
					showNavAsPopperOnly: true
				}
			},
			{
				path: 'payment/analytics',
				name: 'payment-analytics',
				component: () => import(/* webpackChunkName: 'CognitoPayLayout' */ './cognito-pay/CognitoPayLayout.vue'),
				props: route => ({ ...route.query, ...route.params, pageComponent: 'CognitoPayAnalytics' }),
				meta: {
					auth: 'required',
					displayName: 'Analytics',
					minimumRole: 'Administrator',
					showGlobalErrors: true,
					globalErrorFilter: [GlobalError.PaymentAccountDisabled]
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.CognitoPay)
						next();
					else
						next({ path: `/${session.value.orgCode}/home` });
				}
			},
			{
				path: 'payment/orders',
				name: 'payment-orders',
				component: () => import(/* webpackChunkName: 'CognitoPayLayout' */ './cognito-pay/CognitoPayLayout.vue'),
				props: route => ({ ...route.query, ...route.params, pageComponent: 'OrdersTable' }),
				meta: {
					auth: 'required',
					displayName: 'Orders',
					minimumRole: 'Administrator',
					showGlobalErrors: true,
					globalErrorFilter: [GlobalError.PaymentAccountDisabled]
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.CognitoPay)
						next();
					else
						next({ path: `/${session.value.orgCode}/home` });
				}
			},
			{
				path: 'payment/payments',
				name: 'payment-payments',
				component: () => import(/* webpackChunkName: 'CognitoPayLayout' */ './cognito-pay/CognitoPayLayout.vue'),
				props: route => ({ ...route.query, ...route.params, pageComponent: 'CognitoPayPayments' }),
				meta: {
					auth: 'required',
					displayName: 'Payments',
					minimumRole: 'Administrator',
					showGlobalErrors: true,
					globalErrorFilter: [GlobalError.PaymentAccountDisabled]
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.CognitoPay)
						next();
					else
						next({ path: `/${session.value.orgCode}/home` });
				}
			},
			{
				path: 'payment/disputes',
				name: 'payment-disputes',
				component: () => import(/* webpackChunkName: 'CognitoPayLayout' */ './cognito-pay/CognitoPayLayout.vue'),
				props: route => ({ ...route.query, ...route.params, pageComponent: 'CognitoPayDisputes' }),
				meta: {
					auth: 'required',
					displayName: 'Disputes',
					minimumRole: 'Administrator',
					showGlobalErrors: true,
					globalErrorFilter: [GlobalError.PaymentAccountDisabled]
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.CognitoPay)
						next();
					else
						next({ path: `/${session.value.orgCode}/home` });
				}
			},
			{
				path: 'payment/payouts',
				name: 'payment-payouts',
				component: () => import(/* webpackChunkName: 'CognitoPayLayout' */ './cognito-pay/CognitoPayLayout.vue'),
				props: route => ({ ...route.query, ...route.params, pageComponent: 'CognitoPayPayouts' }),
				meta: {
					auth: 'required',
					displayName: 'Payouts',
					minimumRole: 'Administrator',
					showGlobalErrors: true,
					globalErrorFilter: [GlobalError.PaymentAccountDisabled]
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.CognitoPay)
						next();
					else
						next({ path: `/${session.value.orgCode}/home` });
				}
			},
			{
				path: 'payment/account',
				name: 'payment-account',
				component: () => import(/* webpackChunkName: 'CognitoPayLayout' */ './cognito-pay/CognitoPayLayout.vue'),
				props: route => ({ ...route.query, ...route.params, pageComponent: 'CognitoPayAccount' }),
				meta: {
					auth: 'required',
					displayName: 'Account',
					minimumRole: 'Owner',
					showGlobalErrors: true,
					globalErrorFilter: [GlobalError.PaymentAccountDisabled]
				},
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.CognitoPay)
						next();
					else
						next({ path: `/${session.value.orgCode}/home` });
				}
			},
			{
				path: ':formUrlName/build',
				name: 'builder',
				component: () => import(/* webpackChunkName: 'FormBuilder' */ './FormBuilder.vue'),
				props: route => ({ ...route.query, ...route.params }),
				beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav && to.path.endsWith('/'))
						next({ ...to, path: to.path.slice(0, -1) });
					else
						next();
				},
				meta: {
					auth: 'required',
					isRouterLink: true,
					showLoadingIndicator: true,
					showFormHeader: true,
					showNavAsPopperOnly: true
				}
			},
			{
				path: ':formUrlName/publish',
				name: 'publish',
				component: () => import(/* webpackChunkName: 'PublishPage' */ './PublishPage.vue'),
				props: route => ({ ...route.query, ...route.params }),
				beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav && to.path.endsWith('/'))
						next({ ...to, path: to.path.slice(0, -1) });
					else
						next();
				},
				meta: {
					auth: 'required',
					isRouterLink: true,
					showLoadingIndicator: true,
					showFormHeader: true,
					showNavAsPopperOnly: true
				}
			},
			{
				path: 'entryemails/:entryId',
				name: 'entry-emails',
				component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
				props: route => ({ ...route.query, ...route.params }),
				async beforeEnter(to, from, next) {
					if (session.value && session.value.enabledFeatures.AppNav) {
						const components = await import(/* webpackChunkName: 'EntryEmails' */ './support/EntryEmails.vue');
						to.matched[to.matched.length - 1].components = components;
					}
					next();
				},
				meta: {
					auth: 'required'
				}

			},
			{
				path: 'updateemailstatuses/:entryId',
				name: 'update-email-statuses',
				component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required'
				}
			},
			{
				path: ':formUrlName/tosviolation',
				alias: '/forms/tosviolation',
				name: 'tos-violation',
				component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required'
				}
			},
			{
				path: ':formUrlName/passwordviolation',
				alias: '/forms/passwordviolation',
				name: 'password-violation',
				component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required'
				}
			},
			{
				path: ':formUrlName/ccviolation',
				alias: '/forms/ccviolation',
				name: 'cc-violation',
				component: () => import(/* webpackChunkName: 'Admin' */ './Admin.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required'
				}
			},
			{
				path: ':formUrlName/(entries/)?:entryView?/:entryNumber?',
				name: 'entries',
				component: () => import(/* webpackChunkName: 'EntriesPage' */ './EntriesPage.vue'),
				props: route => ({ ...route.query, ...route.params }),
				meta: {
					auth: 'required',
					isRouterLink: true,
					showLoadingIndicator: true,
					customReadyEvent: true,
					showGlobalErrors: true,
					globalErrorExclude: [GlobalError.PaymentAccountDisabled]
				},
				beforeEnter: async (to, _, next) => {
					const session = await getActiveSession();

					if (session?.enabledFeatures?.AppNav && (to.params.pathMatch === 'entries/' || to.params.entryView === 'entries')) {
						delete to.params.pathMatch;
						if (to.params.entryView === 'entries')
							delete to.params.entryView;
						next(to);
					}
					else
						next();
				}
			}
		]
	}
];
